import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of, catchError, map, mergeMap, concatMap, filter } from 'rxjs';
import { studentActions } from '../actions/student.actions';
import { selectLoading } from '../selectors/student.selector';
import { HttpService } from '../services/http.service';
import { finalizeLoader } from '@app/shared/components';

@Injectable()
export class StudentEffects {
    private readonly store = inject(Store);
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);

    getStudent$ = createEffect(() => {
        return this.actions.pipe(
            ofType(studentActions.getStudent),
            concatLatestFrom(({ id }) => this.store.select(selectLoading(id))),
            filter(([, loading]) => loading === true),
            mergeMap(([{ id }]) => {
                return this.httpService.getStudent(id).pipe(
                    finalizeLoader('student-get'),
                    map((payload) => studentActions.getStudentSuccess({ payload })),
                    catchError(() => of(studentActions.getStudentError({ id }))),
                );
            }),
        );
    });

    addStudent$ = createEffect(() => {
        return this.actions.pipe(
            ofType(studentActions.addStudent),
            concatMap(({ value }) => {
                return this.httpService.addStudent(value).pipe(
                    finalizeLoader('student-add'),
                    map(() => studentActions.addStudentSuccess()),
                    catchError(() => of(studentActions.addStudentError())),
                );
            }),
        );
    });

    updateStudent$ = createEffect(() => {
        return this.actions.pipe(
            ofType(studentActions.updateStudent),
            mergeMap(({ id, value }) => {
                return this.httpService.updateStudent(id, value).pipe(
                    finalizeLoader('student-update'),
                    map(() => studentActions.updateStudentSuccess()),
                    catchError(() => of(studentActions.updateStudentError())),
                );
            }),
        );
    });

    deleteStudent$ = createEffect(() => {
        return this.actions.pipe(
            ofType(studentActions.deleteStudent),
            mergeMap(({ id }) => {
                return this.httpService.deleteStudent(id).pipe(
                    finalizeLoader('student-delete'),
                    map(() => studentActions.deleteStudentSuccess()),
                    catchError(() => of(studentActions.deleteStudentError())),
                );
            }),
        );
    });
}
